import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Tracking } from '../../../../../../../../../global/tracking/tracking';
import { ButtonComponent } from '../../../../../../../../../shared/components/button/button.component';
import { DialogService } from '../../../../../../../../../shared/components/dialog/services/dialog.service';
import { LoadingSpinnerComponent } from '../../../../../../../../../shared/components/forms/loading-spinner/loading-spinner.component';
import { QMEncryptDirective } from '../../../../../../../../../shared/directives/quantum-metric.directive';
import { BrowserSharedService } from '../../../../../../../../../shared/services/browser-shared.service';
import {
  DialogAction,
  InteractionMode,
  Voice2VoiceDialogComponent,
} from '../../smart-fix-voice2voice-dialogs/voice2voice-dialogs-component.component';
import { SmartFixAnalyticsService } from '../../utils/analytics/smart-fix-analytics-service';
import {
  capitaliseSentenceStarts,
  generateBedrockMessage,
  normaliseBrandName,
} from '../../utils/helpers/bedrock-messages-helper';
import { BedrockMessages } from '../../utils/interfaces/bedrock-chat.interface';
import { NovaSonicVoiceService } from '../../utils/nova-sonic-voice.service';
import { VoiceCapsuleComponent } from './voice-capsule/voice-capsule.component';

@Component({
  selector: 'app-smart-fix-voice-chat',
  imports: [
    CommonModule,
    QMEncryptDirective,
    ButtonComponent,
    ButtonComponent,
    LoadingSpinnerComponent,
    VoiceCapsuleComponent,
  ],
  templateUrl: './smart-fix-voice-chat.component.html',
  styleUrl: './smart-fix-voice-chat.component.scss',
})
export class SmartFixVoiceChatComponent implements OnDestroy, AfterViewInit {
  // 8 minutes
  private static readonly VOICE_SESSION_DURATION_SECONDS = 8 * 60;
  // show when 60 seconds left of session
  private static readonly COUNTDOWN_BANNER_THRESHOLD_SECONDS = 60;

  // Tracks the manual Nova Sonic subscriptions so ngOnDestroy can unsubscribe
  // them. NovaSonicVoiceService is a root singleton, so without this,
  // recreating this component would leave old subscriptions alive alongside
  // new ones — every emission would be handled twice, duplicating messages.
  private readonly subscriptions = new Subscription();
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private loadingCopyTimer: ReturnType<typeof setTimeout> | null = null;
  novaSonicTesting = true; // set to true for now
  private novaSonicErrorDialogOpen = false;
  private countdownBannerDismissed = false;
  private trackedSessionStartedAt: number | null = null;
  showCountdownBanner = false;
  countdownSecondsRemaining = SmartFixVoiceChatComponent.VOICE_SESSION_DURATION_SECONDS;

  scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  showTopFade = signal(false);
  showDelayedLoadingCopy = signal(false);

  // Bound once so ngOnDestroy can remove the exact same listener reference.
  private readonly handleTranscriptScroll = (): void => this.checkScrollable();

  // ── Inputs ────────────────────────────────────────────────────────────────
  interactionMode = input<'Voice to Voice' | 'Text'>('Text');
  chatHistory = input<BedrockMessages[]>([]);
  sessionId = input<string>('');
  planNumber = input<string>('');
  claimBundleData = input<any>();
  V2VisLoading = input<boolean>(false);

  // ── Outputs ───────────────────────────────────────────────────────────────
  addMessage = output<BedrockMessages>();
  goToRepairBooking = output<void>();
  switchToTextMode = output<'Voice to Voice' | 'Text'>();

  // ── Derived state ─────────────────────────────────────────────────────────
  get isNovaSonicSelected() {
    return this.interactionMode() === 'Voice to Voice';
  }

  get isNovaSonicActive() {
    return this.isNovaSonicSelected && this.novaSonicService.status() === 'connected';
  }

  // Merges consecutive same-role messages into a single block so the voice
  // transcript shows one bubble per "turn" instead of one bubble per message
  // (Nova Sonic streams a turn as several separate chat entries).
  protected voiceChatGroups = computed(() => {
    const groups: { role: string; messages: string[] }[] = [];

    for (const chat of this.chatHistory()) {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.role === chat.role) {
        lastGroup.messages.push(chat.content.message);
      } else {
        groups.push({ role: chat.role, messages: [chat.content.message] });
      }
    }

    return groups.map((group) => ({
      role: group.role,
      text: capitaliseSentenceStarts(group.messages.join(' ')),
    }));
  });

  private get productType(): string {
    return this.claimBundleData()?.bundle?.productType ?? '';
  }

  private get brand(): string {
    const brand = this.claimBundleData()?.bundle?.manufacturer ?? '';
    return normaliseBrandName(brand);
  }

  constructor(
    readonly novaSonicService: NovaSonicVoiceService,
    private smartFixAnalyticsService: SmartFixAnalyticsService,
    private dialog: DialogService,
    private browserSharedService: BrowserSharedService
  ) {
    // Voice-to-voice's mobile waveform is a fixed bar pinned to the true
    // bottom of the page — the global footer (rendered by AppComponent,
    // outside this component's tree) would otherwise sit between the chat
    // and it. The global header swaps its D&G logo for the "Smart-fix
    // Voice" mark too, on mobile (see HeaderComponent). browserSharedService
    // is the bridge to both for as long as voice-to-voice is selected.
    effect(() => {
      const isVoiceToVoice = this.interactionMode() === 'Voice to Voice';
      this.browserSharedService.hideFooter.set(isVoiceToVoice);
      this.browserSharedService.showVoiceBranding.set(isVoiceToVoice);
    });

    // Scroll the voice transcript to the bottom whenever a new message arrives.
    // Only do this while the user is already at/near the bottom — otherwise a
    // message arriving while they've scrolled up would yank them straight back.
    effect(() => {
      if (this.chatHistory().length > 0) {
        this.scrollToBottom();
      }
    });

    effect(() => {
      const sessionStartedAt = this.novaSonicService.sessionStartedAt();

      if (sessionStartedAt !== null && sessionStartedAt !== this.trackedSessionStartedAt) {
        this.trackedSessionStartedAt = sessionStartedAt;
        this.startSessionCountdownTimer();
        // Play the two-note "ready" chime with a short delay to align with
        // the capsule's grow-in transition (replaces the old transitionend
        // listener which depended on the removed .voice-orb CSS transition).
        setTimeout(() => this.playReadinessChime(), 420);
        return;
      }

      if (sessionStartedAt === null && this.trackedSessionStartedAt !== null) {
        this.trackedSessionStartedAt = null;
        this.clearSessionCountdownTimer();
      }
    });

    effect(() => {
      const loading = this.V2VisLoading();

      if (loading) {
        if (this.loadingCopyTimer) return;

        // show new copy after 3 seconds
        this.loadingCopyTimer = setTimeout(() => {
          this.showDelayedLoadingCopy.set(true);
        }, 3000);
        return;
      }

      this.clearLoadingCopyTimer();
      this.showDelayedLoadingCopy.set(false);
    });

    // Subscribe to Nova Sonic text output and emit as chat messages
    this.subscriptions.add(
      this.novaSonicService.textOutput$.subscribe(({ text, messageId }) => {
        const msg = generateBedrockMessage(
          'agent',
          this.sessionId() || '',
          { message: text, type: 'text' },
          false,
          messageId
        );
        this.addMessage.emit(msg);
      })
    );

    // Show the user's own spoken words as a user bubble in the chat
    this.subscriptions.add(
      this.novaSonicService.userText$.subscribe(({ text, messageId }) => {
        const msg = generateBedrockMessage(
          'user',
          this.sessionId() || '',
          { message: text, type: 'voice_to_voice' },
          false,
          messageId
        );
        this.addMessage.emit(msg);
      })
    );

    this.subscriptions.add(
      this.novaSonicService.error$.subscribe(() => {
        // ignore the error event if already in text mode so the dialog doesnt show again
        if (this.interactionMode() !== 'Voice to Voice') return;
        if (this.V2VisLoading()) return;
        this.showNovaSonicErrorAndSwitch();
      })
    );
  }

  ngAfterViewInit(): void {
    const containerRef = this.scrollContainer()?.nativeElement;
    if (containerRef) {
      containerRef.addEventListener('scroll', this.handleTranscriptScroll);
      this.checkScrollable();
    }
  }

  ngOnDestroy(): void {
    this.clearSessionCountdownTimer();
    this.clearLoadingCopyTimer();
    this.subscriptions.unsubscribe();
    this.scrollContainer()?.nativeElement.removeEventListener('scroll', this.handleTranscriptScroll);
    // Component is going away entirely (e.g. navigating off the page) —
    // don't leave the global footer hidden or the header's logo swapped.
    this.browserSharedService.hideFooter.set(false);
    this.browserSharedService.showVoiceBranding.set(false);
  }

  toggleMicMute(): void {
    this.novaSonicService.toggleMicMute();
  }

  async promptLeaveVoiceChat(): Promise<void> {
    this.dialog
      .openDialog(Voice2VoiceDialogComponent, {
        data: {
          ariaRole: 'dialog',
          titleText: 'Leave voice chat?',
          descriptionText: `You won't be able to come back into voice chat if you leave now. Continue with text chat, or carry on with your repair booking.`,
          iconName: 'arrow-left-from-bracket',
          firstButton: { text: 'Switch to text chat', variant: 'secondary', mode: 'Text' },
          secondButton: {
            text: 'Continue repair booking',
            variant: 'secondary',
            action: 'repair_booking',
          },
        },
      })
      .closed.subscribe(async (result: unknown) => {
        const dialog = result as { mode?: InteractionMode; action?: DialogAction } | null;
        if (dialog?.action === 'repair_booking') {
          await this.stopVoiceSessionAndTrack();
          this.smartFixAnalyticsService.triggerSmartFixEvent(
            'Continue repair booking',
            '',
            'voice chat leave',
            'SF genAI',
            'claims-sf-genAI'
          );
          this.goToRepairBooking.emit();
        } else if (dialog?.mode === 'Text') {
          await this.stopVoiceSessionAndTrack();
          this.smartFixAnalyticsService.triggerSmartFixEvent(
            'Switch to text chat',
            '',
            'voice chat leave',
            'SF genAI',
            'claims-sf-genAI'
          );
          this.smartFixAnalyticsService.triggerSmartFixEvent(
            'Yes',
            'fault-details-completed',
            'banner - genAI',
            'SF genAI',
            'claims-sf-genAI'
          );
          this.switchToTextMode.emit('Text');
        }
      });
  }

  // stops conversation and tracks with PH
  private async stopVoiceSessionAndTrack(): Promise<void> {
    const voiceSessionStartedAt = this.novaSonicService.sessionStartedAt();
    await this.novaSonicService.stop();
    const timeNovaSonicStopped = Date.now();

    const novaSonicSessionDuration =
      voiceSessionStartedAt !== null
        ? Math.max(0, Math.round((timeNovaSonicStopped - voiceSessionStartedAt) / 1000))
        : 0;

    Tracking.capture('smart_fix_stop_voice2voice', {
      productType: this.productType,
      brand: this.brand,
      voice2voiceSessionDuration: `${novaSonicSessionDuration} seconds`,
    });
  }

  async switchToTextModeAfterStop(): Promise<void> {
    await this.stopVoiceSessionAndTrack();
    this.switchToTextMode.emit('Text');
  }

  // mid convo failure path
  private showNovaSonicErrorAndSwitch(): void {
    if (this.novaSonicErrorDialogOpen || this.interactionMode() !== 'Voice to Voice') {
      return;
    }

    this.novaSonicErrorDialogOpen = true;

    const dialogRef = this.dialog.openDialog(Voice2VoiceDialogComponent, {
      data: {
        ariaRole: 'alertdialog',
        titleText: 'Voice mode unavailable',
        descriptionText: `We could not connect to voice mode. You can continue in text mode.`,
        iconName: 'exclamation',
        isErrorDialog: true,
        autoSwitchCountdownSeconds: 8,
        autoSwitchCountdownPrefix: 'We will switch you over in',
        firstButton: { text: 'Switch to text mode', variant: 'secondary', mode: 'Text' },
      },
      autoFocus: false,
    });

    const autoSwitchTimer = setTimeout(() => {
      dialogRef.close({ mode: 'Text' });
    }, 8000);

    dialogRef.closed.subscribe(() => {
      clearTimeout(autoSwitchTimer);
      this.novaSonicErrorDialogOpen = false;
      this.switchToTextModeAfterStop();
    });
  }

  private scrollToBottom(): void {
    const containerRef = this.scrollContainer()?.nativeElement;
    if (!containerRef) return;

    this.checkScrollable();

    requestAnimationFrame(() => {
      setTimeout(() => {
        const isScrollable = containerRef.scrollHeight > containerRef.clientHeight;
        if (isScrollable) {
          containerRef.scrollTo({ top: containerRef.scrollHeight, behavior: 'smooth' });
        } else {
          containerRef.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 0);
    });
  }

  closeCountdownBanner(): void {
    this.countdownBannerDismissed = true;
    this.showCountdownBanner = false;
  }

  get formattedCountdownSecondsRemaining(): string {
    const totalSeconds = Math.max(0, this.countdownSecondsRemaining);
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  private startSessionCountdownTimer(): void {
    this.clearSessionCountdownTimer();
    this.countdownBannerDismissed = false;
    this.countdownSecondsRemaining = SmartFixVoiceChatComponent.VOICE_SESSION_DURATION_SECONDS;
    this.showCountdownBanner = false;

    this.updateSessionCountdown();
    this.countdownTimer = setInterval(() => {
      this.updateSessionCountdown();
    }, 1000);
  }

  private updateSessionCountdown(): void {
    const sessionStartedAt = this.novaSonicService.sessionStartedAt();
    if (sessionStartedAt === null) {
      this.showCountdownBanner = false;
      return;
    }

    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - sessionStartedAt) / 1000));
    const remainingSeconds = Math.max(0, SmartFixVoiceChatComponent.VOICE_SESSION_DURATION_SECONDS - elapsedSeconds);

    this.countdownSecondsRemaining = remainingSeconds;
    this.showCountdownBanner =
      !this.countdownBannerDismissed &&
      remainingSeconds > 0 &&
      remainingSeconds <= SmartFixVoiceChatComponent.COUNTDOWN_BANNER_THRESHOLD_SECONDS;

    if (remainingSeconds <= 0) {
      this.clearSessionCountdownTimer();
    }
  }

  private clearSessionCountdownTimer(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    this.showCountdownBanner = false;
  }

  private clearLoadingCopyTimer(): void {
    if (this.loadingCopyTimer) {
      clearTimeout(this.loadingCopyTimer);
      this.loadingCopyTimer = null;
    }
  }

  // Top fade shows once scrolled past the start; bottom fade shows while
  // there's still more content below — thresholds match the fade heights in
  // the stylesheet (48px top / 24px bottom) so the fade never dims content
  // that's still fully visible.
  private checkScrollable(): void {
    const containerRef = this.scrollContainer()?.nativeElement;
    if (!containerRef) return;

    const isScrollable = containerRef.scrollHeight > containerRef.clientHeight;
    this.showTopFade.set(isScrollable && containerRef.scrollTop > 0);
  }

  // Short two-note "ready" cue played once the orb reaches full size, per the
  // intro-animation design spec. Generated with the Web Audio API rather than
  // a bundled audio asset — kept best-effort since browsers may still block
  // it if this fires too far outside a user gesture.
  private playReadinessChime(): void {
    try {
      const AudioContextCtor = window.AudioContext ?? (window as any).webkitAudioContext;
      if (!AudioContextCtor) return;

      const ctx = new AudioContextCtor();
      const now = ctx.currentTime;

      [523.25, 659.25].forEach((frequency, i) => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;

        const start = now + i * 0.12;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.15, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);

        oscillator.connect(gain).connect(ctx.destination);
        oscillator.start(start);
        oscillator.stop(start + 0.3);
      });

      setTimeout(() => void ctx.close(), 500);
    } catch (err) {
      console.error('Nova Sonic: failed to play readiness chime', err);
    }
  }
}
